import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BarChart3, Calendar, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../stores';
import API_BASE_URL from '../config/api';

interface StatsData {
  llm: Array<{
    provider: string;
    model: string;
    total_requests: string;
    total_tokens: string;
    total_cost_rub: string;
    avg_response_time_ms: string;
  }>;
  tts: Array<{
    service: string;
    total_requests: string;
    total_characters: string;
    total_cost_rub: string;
  }>;
  stt: Array<{
    service: string;
    total_requests: string;
    total_audio_duration_sec: string;
    total_cost_rub: string;
  }>;
  totalCost: {
    llm: number;
    tts: number;
    stt: number;
    total: number;
  };
}

interface RecentOperations {
  llm?: Array<{
    id: string;
    provider: string;
    model: string;
    total_tokens: number;
    estimated_cost_rub: string;
    response_time_ms: number;
    is_mock: number;
    created_at: string;
    user_id: string | null;
    username: string | null;
  }>;
  tts?: Array<{
    id: string;
    service: string;
    characters_used: number;
    estimated_cost_rub: string;
    response_time_ms: number;
    created_at: string;
    user_id: string | null;
    username: string | null;
  }>;
  stt?: Array<{
    id: string;
    service: string;
    audio_duration_sec: string;
    estimated_cost_rub: string;
    response_time_ms: number;
    confidence: string;
    created_at: string;
    user_id: string | null;
    username: string | null;
  }>;
}

type PeriodPreset = 'today' | 'week' | 'month' | 'custom';

const StatisticsPage: React.FC = () => {
  const { token } = useAuthStore();
  const [period, setPeriod] = useState<PeriodPreset>('week');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stats, setStats] = useState<StatsData | null>(null);
  const [recentOps, setRecentOps] = useState<RecentOperations | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePeriodChange = (newPeriod: PeriodPreset) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const calculatePeriodDays = (): number => {
    if (period === 'custom' && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    }
    
    switch (period) {
      case 'today': return 1;
      case 'week': return 7;
      case 'month': return 30;
      default: return 7;
    }
  };

  const fetchStatistics = async () => {
    setIsLoading(true);
    setError(null);
    
    if (!token) {
      setError('Требуется авторизация');
      setIsLoading(false);
      return;
    }
    
    try {
      const days = calculatePeriodDays();
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Загружаем агрегированную статистику
      const statsResponse = await fetch(`${API_BASE_URL}/api/stats?period=${days}`, { headers });
      if (!statsResponse.ok) {
        throw new Error('Ошибка загрузки статистики');
      }
      const statsResult = await statsResponse.json();
      setStats(statsResult.data);
      
      // Загружаем последние операции (с тем же периодом!)
      const recentResponse = await fetch(`${API_BASE_URL}/api/stats/recent?limit=10&period=${days}`, { headers });
      if (recentResponse.ok) {
        const recentResult = await recentResponse.json();
        setRecentOps(recentResult.data);
      } else {
        console.warn('Не удалось загрузить последние операции:', recentResponse.status);
        setRecentOps(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
      console.error('Ошибка загрузки статистики:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Автоматическая загрузка статистики при монтировании компонента
  useEffect(() => {
    if (token) {
      fetchStatistics();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatNumber = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '0' : num.toLocaleString('ru-RU');
  };

  const formatCost = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  // Для подробной статистики - показываем точные значения (4 знака)
  const formatDetailedCost = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '0.0000' : num.toFixed(4);
  };

  return (
    <Container>
      <Header>
        <Title>
          <BarChart3 size={28} />
          Статистика использования
        </Title>
      </Header>

      <FilterSection>
        <FilterTitle>Период:</FilterTitle>
        
        <PresetButtons>
          <PresetButton
            active={period === 'today'}
            onClick={() => handlePeriodChange('today')}
          >
            Сегодня
          </PresetButton>
          <PresetButton
            active={period === 'week'}
            onClick={() => handlePeriodChange('week')}
          >
            Последняя неделя
          </PresetButton>
          <PresetButton
            active={period === 'month'}
            onClick={() => handlePeriodChange('month')}
          >
            Текущий месяц
          </PresetButton>
          <PresetButton
            active={period === 'custom'}
            onClick={() => handlePeriodChange('custom')}
          >
            Выбрать даты
          </PresetButton>
        </PresetButtons>

        {period === 'custom' && (
          <DateRangeContainer>
            <DateInput>
              <label>
                <Calendar size={16} />
                С:
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </DateInput>
            <DateInput>
              <label>
                <Calendar size={16} />
                По:
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </DateInput>
          </DateRangeContainer>
        )}

        <ShowButton onClick={fetchStatistics} disabled={isLoading}>
          {isLoading ? (
            <>
              <RefreshCw size={16} className="spinning" />
              Загрузка...
            </>
          ) : (
            <>
              <BarChart3 size={16} />
              Показать
            </>
          )}
        </ShowButton>
      </FilterSection>

      {error && (
        <ErrorMessage>
          ⚠️ {error}
        </ErrorMessage>
      )}

      {stats && (
        <>
          {/* LLM Статистика */}
          {stats.llm && stats.llm.length > 0 && (
            <Section>
              <SectionTitle>💬 LLM (Языковые модели)</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <th>Провайдер</th>
                    <th>Модель</th>
                    <th>Запросов</th>
                    <th>Токенов</th>
                    <th>Время (мс)</th>
                    <th>Стоимость (₽)</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.llm.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.provider}</td>
                      <td>{row.model}</td>
                      <td>{formatNumber(row.total_requests)}</td>
                      <td>{formatNumber(row.total_tokens)}</td>
                      <td>{formatNumber(row.avg_response_time_ms)}</td>
                      <td>{formatCost(row.total_cost_rub)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}><strong>Итого:</strong></td>
                    <td>
                      <strong>
                        {formatNumber(
                          stats.llm.reduce((sum, row) => 
                            sum + parseInt(row.total_tokens || '0'), 0
                          )
                        )}
                      </strong>
                    </td>
                    <td></td>
                    <td>
                      <strong>
                        {formatCost(
                          stats.llm.reduce((sum, row) => 
                            sum + parseFloat(row.total_cost_rub || '0'), 0
                          )
                        )}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </Section>
          )}

          {/* TTS Статистика */}
          {stats.tts && stats.tts.length > 0 && (
            <Section>
              <SectionTitle>🔊 TTS (Синтез речи)</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <th>Сервис</th>
                    <th>Запросов</th>
                    <th>Символов</th>
                    <th>Стоимость (₽)</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.tts.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.service}</td>
                      <td>{formatNumber(row.total_requests)}</td>
                      <td>{formatNumber(row.total_characters)}</td>
                      <td>{formatCost(row.total_cost_rub)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}><strong>Итого:</strong></td>
                    <td>
                      <strong>
                        {formatNumber(
                          stats.tts.reduce((sum, row) => 
                            sum + parseInt(row.total_characters || '0'), 0
                          )
                        )}
                      </strong>
                    </td>
                    <td>
                      <strong>
                        {formatCost(
                          stats.tts.reduce((sum, row) => 
                            sum + parseFloat(row.total_cost_rub || '0'), 0
                          )
                        )}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </Section>
          )}

          {/* STT Статистика */}
          {stats.stt && stats.stt.length > 0 && (
            <Section>
              <SectionTitle>🎤 STT (Распознавание речи)</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <th>Сервис</th>
                    <th>Запросов</th>
                    <th>Длительность (сек)</th>
                    <th>Стоимость (₽)</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.stt.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.service}</td>
                      <td>{formatNumber(row.total_requests)}</td>
                      <td>{formatNumber(row.total_audio_duration_sec)}</td>
                      <td>{formatCost(row.total_cost_rub)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}><strong>Итого:</strong></td>
                    <td>
                      <strong>
                        {formatNumber(
                          stats.stt.reduce((sum, row) => 
                            sum + parseFloat(row.total_audio_duration_sec || '0'), 0
                          )
                        )}
                      </strong>
                    </td>
                    <td>
                      <strong>
                        {formatCost(
                          stats.stt.reduce((sum, row) => 
                            sum + parseFloat(row.total_cost_rub || '0'), 0
                          )
                        )}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </Section>
          )}

          {/* Общая стоимость */}
          <TotalCostSection>
            <TotalCostTitle>💰 Общая стоимость</TotalCostTitle>
            <CostGrid>
              <CostCard>
                <CostLabel>LLM</CostLabel>
                <CostValue>{formatCost(stats.totalCost.llm)} ₽</CostValue>
              </CostCard>
              <CostCard>
                <CostLabel>TTS</CostLabel>
                <CostValue>{formatCost(stats.totalCost.tts)} ₽</CostValue>
              </CostCard>
              <CostCard>
                <CostLabel>STT</CostLabel>
                <CostValue>{formatCost(stats.totalCost.stt)} ₽</CostValue>
              </CostCard>
              <CostCard highlight>
                <CostLabel>ИТОГО</CostLabel>
                <CostValue>{formatCost(stats.totalCost.total)} ₽</CostValue>
              </CostCard>
            </CostGrid>
          </TotalCostSection>

          {/* Последние операции */}
          <Section>
            <SectionTitle>📋 Последние 10 операций</SectionTitle>
            
            {recentOps && (recentOps.llm?.length || recentOps.tts?.length || recentOps.stt?.length) ? (
              <>
              
              {/* Последние LLM запросы */}
              {recentOps.llm && recentOps.llm.length > 0 && (
                <RecentTable>
                  <RecentTableTitle>💬 LLM запросы</RecentTableTitle>
                  <Table>
                    <thead>
                      <tr>
                        <th>Время</th>
                        <th>Пользователь</th>
                        <th>Провайдер</th>
                        <th>Модель</th>
                        <th>Токены</th>
                        <th>Время (мс)</th>
                        <th>Стоимость (₽)</th>
                        <th>Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOps.llm.map((op) => (
                        <tr key={op.id}>
                          <td>{new Date(op.created_at).toLocaleString('ru-RU', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}</td>
                          <td>{op.username || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Гость</span>}</td>
                          <td>{op.provider}</td>
                          <td>{op.model}</td>
                          <td>{formatNumber(op.total_tokens)}</td>
                          <td>{formatNumber(op.response_time_ms)}</td>
                          <td>{formatDetailedCost(op.estimated_cost_rub)}</td>
                          <td>
                            {op.is_mock === 1 ? (
                              <StatusBadge color="#f59e0b">Мок</StatusBadge>
                            ) : (
                              <StatusBadge color="#3b82f6">API</StatusBadge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </RecentTable>
              )}
              
              {/* Последние TTS запросы */}
              {recentOps.tts && recentOps.tts.length > 0 && (
                <RecentTable>
                  <RecentTableTitle>🔊 TTS запросы</RecentTableTitle>
                  <Table>
                    <thead>
                      <tr>
                        <th>Время</th>
                        <th>Пользователь</th>
                        <th>Сервис</th>
                        <th>Символов</th>
                        <th>Время (мс)</th>
                        <th>Стоимость (₽)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOps.tts.map((op) => (
                        <tr key={op.id}>
                          <td>{new Date(op.created_at).toLocaleString('ru-RU', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}</td>
                          <td>{op.username || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Гость</span>}</td>
                          <td>{op.service}</td>
                          <td>{formatNumber(op.characters_used)}</td>
                          <td>{formatNumber(op.response_time_ms)}</td>
                          <td>{formatDetailedCost(op.estimated_cost_rub)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </RecentTable>
              )}
              
              {/* Последние STT запросы */}
              {recentOps.stt && recentOps.stt.length > 0 && (
                <RecentTable>
                  <RecentTableTitle>🎤 STT запросы</RecentTableTitle>
                  <Table>
                    <thead>
                      <tr>
                        <th>Время</th>
                        <th>Пользователь</th>
                        <th>Сервис</th>
                        <th>Длительность (сек)</th>
                        <th>Точность</th>
                        <th>Время (мс)</th>
                        <th>Стоимость (₽)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOps.stt.map((op) => (
                        <tr key={op.id}>
                          <td>{new Date(op.created_at).toLocaleString('ru-RU', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}</td>
                          <td>{op.username || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Гость</span>}</td>
                          <td>{op.service}</td>
                          <td>{formatNumber(op.audio_duration_sec)}</td>
                          <td>{(parseFloat(op.confidence) * 100).toFixed(1)}%</td>
                          <td>{formatNumber(op.response_time_ms)}</td>
                          <td>{formatDetailedCost(op.estimated_cost_rub)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </RecentTable>
              )}
              </>
            ) : (
              <EmptyState>
                <BarChart3 size={48} />
                <p>Нет подробных данных за выбранный период</p>
                <small>Подробная статистика будет доступна после выполнения операций</small>
              </EmptyState>
            )}
          </Section>

          {/* Сообщение если нет данных */}
          {(!stats.llm || stats.llm.length === 0) && 
           (!stats.tts || stats.tts.length === 0) && 
           (!stats.stt || stats.stt.length === 0) && (
            <EmptyState>
              <BarChart3 size={64} />
              <p>Нет данных за выбранный период</p>
              <small>Сделайте запросы к LLM, TTS или STT для накопления статистики</small>
            </EmptyState>
          )}
        </>
      )}

      {!stats && !isLoading && !error && (
        <EmptyState>
          <BarChart3 size={64} />
          <p>Выберите период и нажмите "Показать"</p>
        </EmptyState>
      )}
    </Container>
  );
};

// Styled Components

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const FilterTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1rem 0;
`;

const PresetButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const PresetButton = styled.button<{ active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 2px solid ${props => props.active ? '#0284c7' : '#e5e7eb'};
  background: ${props => props.active ? '#0284c7' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #0284c7;
    background: ${props => props.active ? '#0369a1' : '#f0f9ff'};
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const DateInput = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-weight: 500;
    color: #374151;
  }

  input {
    padding: 0.5rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    font-size: 0.875rem;
  }
`;

const ShowButton = styled.button`
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  border: none;
  background: #0284c7;
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #0369a1;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  th, td {
    padding: 0.75rem 1rem;
    text-align: left;
  }

  thead {
    background: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
  }

  th {
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
    text-transform: uppercase;
  }

  tbody tr {
    border-bottom: 1px solid #f3f4f6;

    &:hover {
      background: #f9fafb;
    }
  }

  td {
    color: #111827;
  }

  tfoot {
    background: #f9fafb;
    border-top: 2px solid #e5e7eb;
    font-weight: 600;
  }
`;

const TotalCostSection = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
`;

const TotalCostTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const CostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const CostCard = styled.div<{ highlight?: boolean }>`
  padding: 1rem;
  border-radius: 0.75rem;
  background: ${props => props.highlight ? '#0284c7' : '#f9fafb'};
  border: 2px solid ${props => props.highlight ? '#0284c7' : '#e5e7eb'};
  color: ${props => props.highlight ? 'white' : '#111827'};
`;

const CostLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  opacity: 0.8;
`;

const CostValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #9ca3af;

  svg {
    margin: 0 auto 1rem;
    opacity: 0.3;
  }

  p {
    font-size: 1.125rem;
    font-weight: 500;
    margin: 0 0 0.5rem 0;
  }

  small {
    font-size: 0.875rem;
    opacity: 0.7;
  }
`;

const RecentTable = styled.div`
  margin-bottom: 1.5rem;
`;

const RecentTableTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.75rem 0;
`;

const StatusBadge = styled.span<{ color: string }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  background: ${props => props.color};
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

export default StatisticsPage;


